import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '@/design-system/atoms/Button';
import FilterButton from '@/design-system/atoms/FilterButton/FilterButton';

import { useSiteSearchContext } from '../../contexts/SiteSearchContext';
import styles from './SearchFilters.module.scss';

/**
 * The SearchFilters component displays the search filters tags
 * @param {object} props - The component props
 * @param {string} props.className - Additional class name
 * @param {array} props.clearSearchFilters - The clear search filters callback
 * @param {string} props.initialFilterValue - The initial filter value
 * @param {array} props.filters - The search filters
 * @param {function} props.triggerExecuteFilter - The execute filter callback
 * @return {React.Component} The search filters component
 */
const SearchFilters = ({
    className,
    clearSearchFilters,
    filters,
    initialFilterValue,
    triggerExecuteFilter,
    searchValue,
    ...props
}) => {
    const { getLabelOverride, setCoveoAnalytics } = useSiteSearchContext();

    /**
     * Click handler for the filter button
     * @param {string} value - The filter value
     * @returns {void}
     */
    const handleFilterClick = React.useCallback(
        (filterValue) => {
            if (triggerExecuteFilter) triggerExecuteFilter({ filterValue });
        },
        [triggerExecuteFilter],
    );

    /**
     * Click handler for the clear filters button
     * @returns {void}
     */
    const handleClearFilters = React.useCallback(() => {
        if (clearSearchFilters) {
            setCoveoAnalytics('facetClearAll');
            clearSearchFilters();
        }
    }, [clearSearchFilters, setCoveoAnalytics]);

    return (
        <div className={classnames(styles['search-filters'], className)} {...props}>
            <ul className={styles['search-filters__list']}>
                {Object.keys(filters).map((key, index) => {
                    const filter = filters[key];
                    const { value, state } = filter;
                    const label = getLabelOverride(value);

                    return (
                        <li key={index}>
                            <FilterButton
                                {...filter}
                                label={label}
                                state={state}
                                ariaLabel={`Filter search results by ${value}`}
                                clickCallback={() => handleFilterClick(value)}
                                analytics={{
                                    eventName: 'search',
                                    componentName: 'Tag Filter Set',
                                    interactionType: 'tag_filter',
                                    selector: 'filter',
                                    searchTerm: searchValue,
                                }}
                            />
                        </li>
                    );
                })}
            </ul>
            {initialFilterValue && (
                <Button
                    className={styles['search-filters__clear-button']}
                    buttonStyle={Button.STYLE.TEXT_EXTRA_SMALL}
                    label="Clear Filter"
                    onClick={handleClearFilters}
                />
            )}
        </div>
    );
};

SearchFilters.propTypes = {
    className: PropTypes.string,
    clearSearchFilters: PropTypes.func,
    filters: PropTypes.object,
};

SearchFilters.defaultProps = {
    filters: {},
};

export default SearchFilters;
